import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Disposal } from 'src/app/_models/disposal';
import { Subscription } from 'rxjs';
import { DisposalService } from '../../_services/disposal.service';
import { NotifyService } from 'src/app/_services/notify.service';
import { finalize } from 'rxjs/operators';
import { fadeInOnEnterAnimation } from 'angular-animations';
import { Asset } from 'src/app/_models/asset';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';

@Component({
   selector: 'app-disposal-edit',
   templateUrl: './disposal-edit.component.html',
   styleUrls: ['./disposal-edit.component.scss'],
   animations: [fadeInOnEnterAnimation()],
})
export class DisposalEditComponent implements OnInit, OnDestroy {
   routerAccess = false;
   opened = false;
   isNewMode = true;
   id: number;
   barcode: string;
   ModalTitle = 'Create New Disposal';
   disposalForm: UntypedFormGroup;
   disposal: Disposal;
   loading = false;
   @Output()
   dialogClose: EventEmitter<Disposal> = new EventEmitter<Disposal>();
   private disposalEditSubscription: Subscription;
   constructor(
      private disposalService: DisposalService,
      private fb: UntypedFormBuilder,
      private notify: NotifyService,
      private route: ActivatedRoute,
      private _location: Location
   ) {}

   ngOnInit() {
      // We can access this through routing or an event. If routing has no parameters we know its an event
      this.route.paramMap.subscribe((params) => {
         if (params.get('id')) {
            this.routerAccess = true;
            this.setupComponent(+params.get('id'));
         }
      });

      this.disposalEditSubscription = this.disposalService.disposalSelected.subscribe((d: Disposal) => {
         this.disposal = d;
         if (d) {
            this.setupComponent(d.id);
         } else {
            this.setupComponent(0);
         }
      });
   }
   setupComponent(id: number) {
      this.createForm();
      if (id > 0) {
         this.createForm();
         this.ModalTitle = 'Correct Disposal';
         this.isNewMode = false;
         this.id = id;
         this.disposalForm.controls['barcode'].disable();
         this.disposalService.getDisposal(this.id).subscribe((_disposal: Disposal) => {
            this.disposal = _disposal;
            this.barcode = this.disposal.barcode;
            this.disposalForm.setValue({
               barcode: this.disposal.barcode,
               barcodeValidate: this.disposal.barcode,
               disposalDate: new Date(this.disposal.disposalDate),
               disposalIncome: this.disposal.disposalIncome,
               disposalExpense: this.disposal.disposalExpense,
               reason: this.disposal.reason,
               method: this.disposal.method,
               purchaser: this.disposal.purchaser,
               journalRef: this.disposal.journalRef,
            });

            this.opened = true;
         });
      } else if (id === -1) {
         this.opened = true;
         this.ModalTitle = 'Create Disposal';
         this.isNewMode = true;
         this.createForm();
         this.barcode = this.disposal.barcode;
         this.disposalForm.setValue({
            barcode: this.disposal.barcode,
            barcodeValidate: this.disposal.barcode,
            disposalDate: new Date(this.disposal.disposalDate),
            disposalIncome: 0,
            disposalExpense: 0,
            reason: this.disposal.reason,
            method: '',
            purchaser: '',
            journalRef: '',
         });
         this.disposalForm.controls['barcode'].disable();
      } else {
         this.opened = true;
         this.ModalTitle = 'Create Disposal';
         this.isNewMode = true;
         this.createForm();
         this.disposalForm.setValue({
            barcode: '',
            barcodeValidate: '',
            disposalDate: '',
            disposalIncome: 0,
            disposalExpense: 0,
            reason: '',
            method: '',
            purchaser: '',
            journalRef: '',
         });
         this.disposalForm.controls['barcode'].disable();
      }
   }

   ngOnDestroy() {
      this.disposalEditSubscription.unsubscribe();
   }

   createForm() {
      this.disposalForm = this.fb.group({
         barcode: ['', Validators.compose([Validators.required, Validators.maxLength(50)])],
         barcodeValidate: ['', Validators.compose([Validators.required, Validators.maxLength(50)])],
         disposalDate: ['', Validators.compose([Validators.required])],
         disposalIncome: [0],
         disposalExpense: [0],
         reason: ['', Validators.compose([Validators.required, Validators.maxLength(500)])],
         method: ['', Validators.maxLength(50)],
         purchaser: ['', Validators.maxLength(120)],
         journalRef: [''],
      });
   }
   assetSelected(asset: Asset) {
      if (asset.status !== 'INUSE') {
         const message = 'Only an asset with a status of INUSE can be disposed';
         this.notify.error(message);
      } else {
         this.barcode = asset.assetBarcode;
         this.disposalForm.patchValue({ barcode: asset.assetBarcode, barcodeValidate: asset.assetBarcode });
      }
   }
   close() {
      this.opened = false;
      this.dialogClose.emit(null);
      this.disposalForm.reset();
   }
   save() {
      this.loading = true;
      this.disposal = Object.assign({}, this.disposalForm.value);
      this.disposal.disposalDate = this.disposalForm.value.disposalDate.toLocaleDateString('en-ZA');
      this.disposal.barcode = this.barcode;
      if (this.isNewMode) {
         this.disposalService
            .createDisposal(this.disposal)
            .pipe(
               finalize(() => {
                  this.loading = false;
               })
            )
            .subscribe(
               () => {
                  const message = 'Successfully Created';
                  this.notify.success(message);
                  this.close();
               },
               (error) => {
                  const message = error;
                  this.notify.error(message);
               }
            );
      } else {
         this.disposal.id = this.id;
         this.disposalService
            .updateDisposal(this.id, this.disposal)
            .pipe(
               finalize(() => {
                  this.loading = false;
               })
            )
            .subscribe(
               () => {
                  const message = 'Successfully Updated';
                  this.notify.success(message);
                  this.close();
               },
               (error) => {
                  const message = error;
                  this.notify.error(message);
               }
            );
      }
   }
   back() {
      this._location.back();
   }
}
